import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import Loading from "../../../../common-components/Loading";
import { getIndirectSummaryVsBudgetFringeAPI, downloadSummaryVsBudgetFringeAPI } from "../../../../api/reports-api";

import {
  TABLE_FONT,
  TH_BG,
  CELL_FG,
  EVEN_BG,
  ODD_BG,
  TOTAL_BG,
  TOTAL_BG_ALT,
  ACCENT_COLOR,
  TABLE_BORDER,
  CELL_FG_MUTED,
  CELL_FG_STRONG,
  TH_BG_BAND,
  TH_TEXT,
  TABLE_SCROLL_WRAP_STYLE,
} from "../../reportTableConfig";
import { PL_POS_C, PL_NEG_C } from "../pl-by-program/plByProgramTheme";

const HEADER_BG = TH_BG;
const HEADER_COL = TH_BG;

/* ── Formatter ── */
function fmtI(v) {
  if (v === null || v === undefined) return "";
  const abs = Math.abs(v);
  const s = abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `($${s})` : `$${s}`;
}

/* Normalize a row: handles both "mtdActuals/mtdBudget" and "mtdA/mtdB" field names */
function normalizeRow(r) {
  const a = (k1, k2, k3) => r[k1] ?? r[k2] ?? r[k3] ?? 0;
  return {
    label:       r.label ?? r.type ?? r.costType ?? r.cost_type ?? r.accountId ?? r.account_id ?? r.accountName ?? "",
    mtdActuals:  a("mtdActuals",  "mtdA", "mtd_actuals"),
    mtdBudget:   a("mtdBudget",   "mtdB", "mtd_budget"),
    mtdVariance: a("mtdVariance", "mtdV", "mtd_variance"),
    ytdActuals:  a("ytdActuals",  "ytdA", "ytd_actuals"),
    ytdBudget:   a("ytdBudget",   "ytdB", "ytd_budget"),
    ytdVariance: a("ytdVariance", "ytdV", "ytd_variance"),
    fyFcst:      a("fyFcst",      "fyA",  "fy_fcst"),
    fyBudget:    a("fyBudget",    "fyB",  "fy_budget"),
    fyVariance:  a("fyVariance",  "fyV",  "fy_variance"),
  };
}

function computeTotal(rows) {
  return rows.reduce(
    (acc, r) => ({
      mtdActuals:  acc.mtdActuals  + (r.mtdActuals  || 0),
      mtdBudget:   acc.mtdBudget   + (r.mtdBudget   || 0),
      mtdVariance: acc.mtdVariance + (r.mtdVariance || 0),
      ytdActuals:  acc.ytdActuals  + (r.ytdActuals  || 0),
      ytdBudget:   acc.ytdBudget   + (r.ytdBudget   || 0),
      ytdVariance: acc.ytdVariance + (r.ytdVariance || 0),
      fyFcst:      acc.fyFcst      + (r.fyFcst      || 0),
      fyBudget:    acc.fyBudget    + (r.fyBudget    || 0),
      fyVariance:  acc.fyVariance  + (r.fyVariance  || 0),
    }),
    { mtdActuals:0, mtdBudget:0, mtdVariance:0, ytdActuals:0, ytdBudget:0, ytdVariance:0, fyFcst:0, fyBudget:0, fyVariance:0 },
  );
}

/* ── Styles ── */
const useStyles = makeStyles(() => ({
  svbFilters: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  svbFilterGroup: {
    display: "inline-flex",
    alignItems: "stretch",
    height: 36,
    borderRadius: 28,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    overflow: "hidden",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    flexShrink: 0,
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": { borderColor: "#C7D2FE", boxShadow: "0 3px 10px rgba(74,85,162,0.12)" },
    "&:focus-within": { borderColor: "#A5B4FC", boxShadow: "0 0 0 3px rgba(240,242,255,0.9)" },
  },
  svbFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#4A55A2",
    padding: "0 14px",
    background: "#F0F2FF",
    height: "100%",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    userSelect: "none",
    fontFamily: "'IBM Plex Sans', sans-serif",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  svbFilterSelect: {
    border: "none",
    outline: "none",
    backgroundColor: "#FFFFFF",
    fontSize: 13,
    fontWeight: 400,
    color: "#111111",
    padding: "0 28px 0 14px",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    minWidth: 110,
    height: "100%",
    fontFamily: "'IBM Plex Sans', sans-serif",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2364748B' stroke-width='1.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
  },
}));

const FONT_SVB   = "'IBM Plex Sans', sans-serif";

const TOTAL_FG = CELL_FG_STRONG;

const TYPE_W_SVB = 260;
const COL_W_SVB  = 110;
const Z_H = 6, Z_S = 4, GH_SVB = 36, CH_SVB = 38, RH_SVB = 44;
const TH_SH_G_SVB = "inset 0 -1px 0 #4a4a4c";
const TH_SH_SVB   = "inset 0 -1px 0 #4a4a4c";
const TOTAL_TOP_SVB = GH_SVB + CH_SVB - 1;

const thCoreSVB = (top, h, z, pad, bg) => ({
  position: "sticky", top, zIndex: z, height: h, lineHeight: 1.25,
  boxSizing: "border-box", background: bg, color: TH_TEXT,
  verticalAlign: "middle", padding: pad, whiteSpace: "nowrap",
  borderRight: "1px solid #4a4a4c", borderBottom: "none",
});
const thBandSVB = () => ({
  ...thCoreSVB(0, GH_SVB, Z_H, "7px 14px", HEADER_BG),
  fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase",
  textAlign: "center", boxShadow: TH_SH_G_SVB,
});
const thColSVB = () => ({
  ...thCoreSVB(GH_SVB, CH_SVB, Z_H, "6px 10px", HEADER_COL),
  fontSize: 10, fontWeight: 600, textAlign: "right", letterSpacing: "0.05em",
  textTransform: "uppercase", minWidth: COL_W_SVB, boxShadow: TH_SH_SVB,
});
const tdBaseSVB = (bg, fw = 400, isFirst = false) => ({
  boxSizing: "border-box", background: bg, textAlign: "right", fontWeight: fw,
  padding: "7px 10px", fontSize: 12, color: CELL_FG, whiteSpace: "nowrap",
  verticalAlign: "middle", fontVariantNumeric: "tabular-nums",
  borderBottom: "1px solid #E2E8F0",
  ...(isFirst ? { borderLeft: "2px solid #C8D0DC" } : {}),
});
const tdTypeSVB = (bg) => ({
  ...tdBaseSVB(bg, 500), position: "sticky", left: 0, zIndex: Z_S,
  width: TYPE_W_SVB, minWidth: TYPE_W_SVB, maxWidth: TYPE_W_SVB, textAlign: "left",
  padding: "7px 14px", boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
  overflow: "hidden", textOverflow: "ellipsis",
});
const tdTotalTypeSVB = () => ({
  ...tdTypeSVB(TOTAL_BG), fontWeight: 700, color: TOTAL_FG,
  position: "sticky", top: TOTAL_TOP_SVB, zIndex: Z_S + 2,
  borderBottom: "2px solid #CBD5E1",
  boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.08)",
});
const tdTotalValSVB = (isFirst = false) => ({
  ...tdBaseSVB(TOTAL_BG, 700, isFirst), color: TOTAL_FG,
  position: "sticky", top: TOTAL_TOP_SVB, zIndex: 2,
  borderBottom: "2px solid #CBD5E1",
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
});

const CARD_SVB   = { background: "#FFFFFF", borderRadius: 16, border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden", marginBottom: 20, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" };
const CHEAD_SVB  = { padding: "18px 28px", borderBottom: "2px solid #F0F2F5", background: "#FFFFFF" };
const CTITLE_SVB = { fontSize: 17, fontWeight: 800, color: "#111827", letterSpacing: "-0.02em", fontFamily: FONT_SVB, margin: 0 };
const CMETA_SVB  = { fontSize: 12, color: "#6B7280", fontFamily: FONT_SVB, marginTop: 2 };

function SVBTable({ title, subtitle, rows, apiTotal, typeLabel, month = "Apr", year }) {
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const toggleSort = (key) => setSort((s) =>
    s.key === key ? (s.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: "asc" }) : { key, dir: "asc" }
  );

  useEffect(() => { setPage(1); }, [rows]);

  const TABLE_W    = TYPE_W_SVB + 9 * COL_W_SVB;
  const vColor     = (v) => (v === 0 || v == null ? CELL_FG : v > 0 ? PL_NEG_C : PL_POS_C);
  const total      = apiTotal ?? computeTotal(rows);
  const sortedRows = sort.key === null ? rows : [...rows].sort((a, b) => {
    const mult = sort.dir === "asc" ? 1 : -1;
    if (sort.key === "label") return a.label.localeCompare(b.label) * mult;
    return ((a[sort.key] ?? 0) - (b[sort.key] ?? 0)) * mult;
  });
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const pagedRows  = sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const pageNumbers = (() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const ps = new Set([1, totalPages, safePage, safePage - 1, safePage + 1, safePage - 2, safePage + 2]);
    return [...ps].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  })();
  const pgBtn = (active) => ({
    minWidth: 30, height: 28, padding: "0 6px", border: `1px solid ${active ? TH_BG_BAND : "#E2E8F0"}`,
    borderRadius: 6, background: active ? TH_BG_BAND : "#fff", fontSize: 12,
    color: active ? "#fff" : TH_BG_BAND, cursor: "pointer", fontFamily: FONT_SVB,
    display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, fontWeight: active ? 600 : 400,
  });

  return (
    <div style={CARD_SVB}>
      <div style={CHEAD_SVB}>
        <h2 style={CTITLE_SVB}>{title || "Summary"}</h2>
        {subtitle && <div style={CMETA_SVB}>{subtitle}</div>}
      </div>
      <div style={TABLE_SCROLL_WRAP_STYLE}>
        <table style={{ borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed", fontFamily: FONT_SVB, fontSize: 12, width: "100%", minWidth: TABLE_W }}>
          <colgroup>
            <col style={{ width: TYPE_W_SVB }} />
            {Array.from({ length: 9 }, (_, i) => <col key={i} style={{ width: COL_W_SVB }} />)}
          </colgroup>
          <thead style={{ background: TH_BG }}>
            <tr style={{ height: GH_SVB }}>
              <th style={{ ...thCoreSVB(0, GH_SVB + CH_SVB, Z_H + 1, "7px 14px", HEADER_BG), left: 0, width: TYPE_W_SVB, minWidth: TYPE_W_SVB, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", textAlign: "left", boxShadow: `4px 0 8px -2px rgba(0,0,0,0.12), ${TH_SH_SVB}` }} rowSpan={2}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                  <span>{typeLabel || "Type"}</span>
                  <button onClick={() => toggleSort("label")}
                    title={sort.key === "label" ? (sort.dir === "asc" ? "Sorted A→Z (click for Z→A)" : "Sorted Z→A (click to clear)") : "Sort by Type"}
                    style={{ flexShrink: 0, width: 18, height: 16, border: "none", background: "transparent", cursor: "pointer", padding: 0, borderRadius: 3, color: sort.key === "label" ? "#A5B4FC" : "#fff", opacity: sort.key === "label" ? 1 : 0.45, fontSize: 11, display: "inline-flex", alignItems: "center", justifyContent: "center", textTransform: "none" }}>
                    {sort.key === "label" ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
                  </button>
                </div>
              </th>
              <th colSpan={3} style={thBandSVB()}>MTD {month}-{year}</th>
              <th colSpan={3} style={thBandSVB()}>YTD {month}-{year}</th>
              <th colSpan={3} style={thBandSVB()}>{month} Actuals + Fcst FY-{year}</th>
            </tr>
            <tr style={{ height: CH_SVB }}>
              {[
                ["mtdActuals","Actuals"],["mtdBudget","Budget"],["mtdVariance","Variance"],
                ["ytdActuals","Actuals"],["ytdBudget","Budget"],["ytdVariance","Variance"],
                ["fyFcst","FY"],["fyBudget","Budget"],["fyVariance","Variance"],
              ].map(([k, lbl]) => (
                <th key={k} title={sort.key === k ? (sort.dir === "asc" ? "Sorted ascending" : "Sorted descending") : "Sort"} style={{ ...thColSVB(), cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort(k)}>
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 3, width: "100%" }}>
                    {lbl}
                    <span style={{ fontSize: 9, color: sort.key === k ? "#A5B4FC" : "rgba(255,255,255,0.4)" }}>
                      {sort.key === k ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
                    </span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Sticky total row */}
            <tr style={{ height: RH_SVB }}>
              <td style={tdTotalTypeSVB()}>Total</td>
              <td style={tdTotalValSVB(true)}>{fmtI(total.mtdActuals)}</td>
              <td style={tdTotalValSVB()}>{fmtI(total.mtdBudget)}</td>
              <td style={{ ...tdTotalValSVB(), color: vColor(total.mtdVariance) }}>{fmtI(total.mtdVariance)}</td>
              <td style={tdTotalValSVB(true)}>{fmtI(total.ytdActuals)}</td>
              <td style={tdTotalValSVB()}>{fmtI(total.ytdBudget)}</td>
              <td style={{ ...tdTotalValSVB(), color: vColor(total.ytdVariance) }}>{fmtI(total.ytdVariance)}</td>
              <td style={tdTotalValSVB(true)}>{fmtI(total.fyFcst)}</td>
              <td style={tdTotalValSVB()}>{fmtI(total.fyBudget)}</td>
              <td style={{ ...tdTotalValSVB(), color: vColor(total.fyVariance) }}>{fmtI(total.fyVariance)}</td>
            </tr>
            {/* Data rows */}
            {pagedRows.map((row, i) => {
              const bg = ((safePage - 1) * pageSize + i) % 2 === 0 ? EVEN_BG : ODD_BG;
              return (
                <tr key={i} style={{ height: RH_SVB }}>
                  <td style={tdTypeSVB(bg)} title={row.label}>{row.label}</td>
                  <td style={tdBaseSVB(bg, 400, true)}>{fmtI(row.mtdActuals)}</td>
                  <td style={tdBaseSVB(bg)}>{fmtI(row.mtdBudget)}</td>
                  <td style={{ ...tdBaseSVB(bg), color: vColor(row.mtdVariance) }}>{fmtI(row.mtdVariance)}</td>
                  <td style={tdBaseSVB(bg, 400, true)}>{fmtI(row.ytdActuals)}</td>
                  <td style={tdBaseSVB(bg)}>{fmtI(row.ytdBudget)}</td>
                  <td style={{ ...tdBaseSVB(bg), color: vColor(row.ytdVariance) }}>{fmtI(row.ytdVariance)}</td>
                  <td style={tdBaseSVB(bg, 400, true)}>{fmtI(row.fyFcst)}</td>
                  <td style={tdBaseSVB(bg)}>{fmtI(row.fyBudget)}</td>
                  <td style={{ ...tdBaseSVB(bg), color: vColor(row.fyVariance) }}>{fmtI(row.fyVariance)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Pagination bar */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 20px", borderTop:"1px solid #E2E8F0", background:"#FAFAFA", gap:12, flexWrap:"wrap" }}>
        <span style={{ fontSize:12, color:"#94A3B8", fontFamily:FONT_SVB, whiteSpace:"nowrap" }}>
          {rows.length} row{rows.length !== 1 ? "s" : ""}
        </span>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:12, color:"#94A3B8", fontFamily:FONT_SVB, whiteSpace:"nowrap" }}>Rows Per Page</span>
          <select style={{ height:28, border:"1px solid #E2E8F0", borderRadius:6, fontSize:12, color:"#374151", padding:"0 6px", background:"#fff", cursor:"pointer", outline:"none", fontFamily:FONT_SVB }}
            value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
            {[10, 25, 50, 100, ...(rows.length > 100 ? [rows.length] : [])].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          {totalPages > 1 && (
            <div style={{ display:"flex", alignItems:"center", gap:3 }}>
              <button style={pgBtn(false)} disabled={safePage === 1} onClick={() => setPage(1)}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="7,2 3,6 7,10"/><polyline points="11,2 7,6 11,10"/></svg>
              </button>
              <button style={pgBtn(false)} disabled={safePage === 1} onClick={() => setPage((p) => p - 1)}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="8,2 4,6 8,10"/></svg>
              </button>
              {pageNumbers.map((p, i) => (
                <React.Fragment key={p}>
                  {i > 0 && pageNumbers[i - 1] < p - 1 && <span style={{ fontSize:12, color:"#94A3B8", padding:"0 4px", userSelect:"none" }}>…</span>}
                  <button style={pgBtn(p === safePage)} onClick={() => setPage(p)}>{p}</button>
                </React.Fragment>
              ))}
              <button style={pgBtn(false)} disabled={safePage === totalPages} onClick={() => setPage((p) => p + 1)}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4,2 8,6 4,10"/></svg>
              </button>
              <button style={pgBtn(false)} disabled={safePage === totalPages} onClick={() => setPage(totalPages)}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="5,2 9,6 5,10"/><polyline points="1,2 5,6 1,10"/></svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SummaryVsBudgetFringe({ filters = {}, actualsMonth, onActualsMonthChange, onHeaderMonthChange, downloadRef, setParentDownloading }) {
  const classes = useStyles();
  const [metricType, setMetricType]     = useState("Cost Type");
  const [metricRows, setMetricRows]     = useState([]);
  const [apiTotal,   setApiTotal]       = useState(null);
  const [loading,     setLoading]       = useState(true);
  const [error,       setError]         = useState(null);

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  const handleDownload = async () => {
    setParentDownloading?.(true);
    try {
      const f = { monthName: [actualsMonth], metricSelection: metricType };
      if (filters.year) f.financialYear = String(filters.year);
      if (filters.monthName?.length) f.month = filters.monthName.map((m) => m.charAt(0).toUpperCase() + m.slice(1, 3).toLowerCase());
      else if (filters.quarter?.length) f.quarter = filters.quarter;
      if (filters.costTypeFringe?.length) f.costTypeFringe = filters.costTypeFringe;
      if (filters.account?.length) f.acctIdndname = filters.account;
      const { blob, filename } = await downloadSummaryVsBudgetFringeAPI(f);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (_) { /* silent */ } finally { setParentDownloading?.(false); }
  };
  if (downloadRef) downloadRef.current = handleDownload;

  useEffect(() => {
    if (actualsMonth == null) return;

    setLoading(true);
    setError(null);

    const f = { monthName: [actualsMonth], metricSelection: metricType };
    if (filters.year)                  f.financialYear  = String(filters.year);
    if (filters.monthName?.length)     f.month   = filters.monthName.map((m) => m.charAt(0).toUpperCase() + m.slice(1, 3).toLowerCase());
    else if (filters.quarter?.length)  f.quarter = filters.quarter;
    if (filters.costTypeFringe?.length) f.costTypeFringe  = filters.costTypeFringe;
    if (filters.account?.length) f.acctIdndname = filters.account;

    getIndirectSummaryVsBudgetFringeAPI(f)
      .then((res) => {
        const top = res?.data ?? res ?? {};
        let metricT = [];

        if (Array.isArray(top)) {
          metricT = top;
        } else {
          metricT = Array.isArray(top.metricTable) ? top.metricTable
                  : Array.isArray(top.typeTable)   ? top.typeTable
                  : Array.isArray(top.rows)        ? top.rows
                  : [];
          if (!metricT.length) {
            const found = Object.values(top).find((v) => Array.isArray(v) && v.length > 0);
            if (found) metricT = found;
          }
        }

        const isTotal = (r) => (r.label ?? "").toString().toLowerCase().trim() === "total";
        const totalRow = metricT.find(isTotal);
        const dataRows = metricT.filter((r) => !isTotal(r));
        const accId = (label = "") => label.split(" ")[0];
        setApiTotal(totalRow ? normalizeRow(totalRow) : null);
        setMetricRows(dataRows.map(normalizeRow).sort((a, b) => accId(a.label).localeCompare(accId(b.label))));
        setLoading(false);

        const apiActualsMonth = !Array.isArray(top) ? top.actualsMonth : null;
        onHeaderMonthChange?.(
          Array.isArray(apiActualsMonth) && apiActualsMonth.length
            ? apiActualsMonth[apiActualsMonth.length - 1]
            : null,
        );
      })
      .catch(() => { setError("Failed to load data."); setLoading(false); });
  }, [actualsMonth, metricType, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const _hasUnsupported = filters.department?.length || filters.subDepartment?.length;
  const _hasWorking     = filters.quarter?.length || filters.monthName?.length || filters.costTypeFringe?.length;
  if (_hasUnsupported && !_hasWorking) {
    return (
      <div style={{ padding: 32 }}>
        <div style={{ background: "#fff", borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.10)", padding: "48px 24px", textAlign: "center", color: "#94A3B8", fontSize: 14, fontFamily: FONT_SVB }}>
          No Data
        </div>
      </div>
    );
  }

  const rows = metricRows;
  const fyYear = filters.year || new Date().getFullYear();

  return (
    <div>
      <div className={classes.svbFilters}>
        <div className={classes.svbFilterGroup}>
          <span className={classes.svbFilterLabel}>Actuals Selection</span>
          <select className={classes.svbFilterSelect} value={actualsMonth ?? ""} onChange={(e) => onActualsMonthChange?.(e.target.value)}>
            {MONTHS.map((m) => <option key={m}>{m}</option>)}
          </select>
        </div>
        <div className={classes.svbFilterGroup}>
          <span className={classes.svbFilterLabel}>Metric Selection</span>
          <select className={classes.svbFilterSelect} value={metricType} onChange={(e) => setMetricType(e.target.value)}>
            <option>Cost Type</option>
            <option>Account ID &amp; Name</option>
          </select>
        </div>
      </div>

      {loading || actualsMonth == null ? (
        <Loading />
      ) : error ? (
        <div style={{ padding: "8px 0 12px", fontFamily: FONT_SVB, fontSize: 13, color: "#DC2626" }}>{error}</div>
      ) : (
        <SVBTable
          title={metricType}
          subtitle="Fringe — Actuals Vs Budget"
          rows={rows}
          apiTotal={apiTotal}
          typeLabel={metricType}
          month={actualsMonth}
          year={fyYear}
        />
      )}
    </div>
  );
}
